import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { ApiResponse } from 'types';

/**
 * Per-worker runtime config (cdr-worker, webhook-consumer, …). cpass is the
 * system of record; on save it pushes the row to the voiceflow runtime, where
 * each worker reads it locally (WorkerConfigStore, ~10s cache). Changes apply
 * within ~10s — no redeploy.
 *
 * Backend: teeoff-cpass-backend /workers/config. `config` is a free-form
 * { key: value } object overlaid on the worker's env defaults; a disabled or
 * missing row = pure env defaults.
 */
export interface WorkerConfig {
  id?: number;
  worker_name: string;
  config?: Record<string, any> | null;
  enabled?: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

// Shared so a 403 reads as a refusal rather than a load failure.
const err = (e: any, msg: string) => apiError(e, msg);

export const workerConfigsService = {
  list: async (): Promise<ApiResponse<WorkerConfig[]>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.WORKER_CONFIGS)).data; }
    catch (e: any) { return err(e, 'Failed to fetch worker configs'); }
  },
  get: async (workerName: string): Promise<ApiResponse<WorkerConfig>> => {
    try { return (await axiosInstance.get(API_ENDPOINTS.WORKER_CONFIG_DETAIL(workerName))).data; }
    catch (e: any) { return err(e, 'Failed to fetch worker config'); }
  },
  // Create/update by worker_name (backend upserts).
  upsert: async (data: WorkerConfig): Promise<ApiResponse<WorkerConfig>> => {
    try { return (await axiosInstance.post(API_ENDPOINTS.WORKER_CONFIGS, data)).data; }
    catch (e: any) { return err(e, 'Failed to save worker config'); }
  },
  remove: async (id: number): Promise<ApiResponse<void>> => {
    try { return (await axiosInstance.delete(API_ENDPOINTS.WORKER_CONFIG_BY_ID(id))).data; }
    catch (e: any) { return err(e, 'Failed to delete worker config'); }
  },
};
